import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Search,
  FileText,
  Download,
  Eye,
  X,
  Folder,
  FolderOpen,
  ChevronRight,
  ChevronDown,
  User,
} from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';
import { apiClient, DiagnosticsFile, DiagnosticsFileContent } from '../api/client';
import { AppBadge, AppFilterSelect, AppFilterValue, parseAppFilter } from '../components/AppFlavor';

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

interface UserFolder {
  driverId: string;
  files: DiagnosticsFile[];
  totalSize: number;
  latestUpload: string;
  userEmail?: string;
  userName?: string;
  /** True when any file in the folder came from the FedEx app (CORE-8116). */
  hasFedex: boolean;
}

export default function DiagnosticsList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [files, setFiles] = useState<DiagnosticsFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [truncated, setTruncated] = useState(false);
  const [userInfo, setUserInfo] = useState<Record<string, { email?: string; name?: string }>>({});

  // Filters - initialize from URL params
  const [driverIdFilter, setDriverIdFilter] = useState(searchParams.get('driver_id') || '');
  const [appFilter, setAppFilter] = useState<AppFilterValue>(parseAppFilter(searchParams.get('app')));

  // Expanded folders
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());

  // Modal state
  const [selectedFile, setSelectedFile] = useState<DiagnosticsFileContent | null>(null);
  const [loadingFile, setLoadingFile] = useState(false);

  // Update URL when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (driverIdFilter) params.set('driver_id', driverIdFilter);
    if (appFilter) params.set('app', appFilter);
    setSearchParams(params, { replace: true });
  }, [driverIdFilter, appFilter, setSearchParams]);

  // Auto-expand folder if driver_id is in URL
  useEffect(() => {
    if (driverIdFilter) {
      setExpandedFolders(new Set([driverIdFilter]));
    }
  }, []);

  useEffect(() => {
    async function fetchDiagnostics() {
      setLoading(true);
      try {
        // Fetch ALL files for grouping into folders, in one request (`all`), so a large log set
        // is neither clamped to the worker's 100-per-page cap nor split across rate-limited calls.
        const data = await apiClient.getDiagnostics({
          driver_id: driverIdFilter || undefined,
          app: appFilter || undefined,
          all: true,
        });
        setFiles(data.files);
        setTotal(data.total);
        setTruncated(Boolean(data.truncated));
        setUserInfo(data.userInfo || {});
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load diagnostics');
      } finally {
        setLoading(false);
      }
    }

    const debounce = setTimeout(fetchDiagnostics, 300);
    return () => clearTimeout(debounce);
  }, [driverIdFilter, appFilter]);

  // Group files by driver ID into folders
  const userFolders = useMemo(() => {
    const folderMap = new Map<string, DiagnosticsFile[]>();

    files.forEach((file) => {
      const existing = folderMap.get(file.driverId) || [];
      existing.push(file);
      folderMap.set(file.driverId, existing);
    });

    const folders: UserFolder[] = [];
    folderMap.forEach((userFiles, driverId) => {
      // Sort files by date (newest first)
      userFiles.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

      const info = userInfo[driverId];
      folders.push({
        driverId,
        files: userFiles,
        totalSize: userFiles.reduce((sum, f) => sum + f.size, 0),
        latestUpload: userFiles[0]?.uploaded || '',
        userEmail: info?.email,
        userName: info?.name,
        hasFedex: userFiles.some((f) => f.appFlavor === 'fedex'),
      });
    });

    // Sort folders by latest upload (newest first)
    folders.sort((a, b) => new Date(b.latestUpload).getTime() - new Date(a.latestUpload).getTime());

    return folders;
  }, [files, userInfo]);

  const toggleFolder = (driverId: string) => {
    setExpandedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(driverId)) {
        next.delete(driverId);
      } else {
        next.add(driverId);
      }
      return next;
    });
  };

  const viewFile = async (file: DiagnosticsFile) => {
    setLoadingFile(true);
    try {
      const content = await apiClient.getDiagnosticsFile(file.driverId, file.date, file.appFlavor);
      setSelectedFile(content);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load file');
    } finally {
      setLoadingFile(false);
    }
  };

  const downloadFile = (content: DiagnosticsFileContent) => {
    const blob = new Blob([content.content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    // FedEx logs get their own name so a driver's Locate2u + FedEx logs for one day don't collide.
    const flavorSuffix = content.appFlavor === 'fedex' ? '_fedex' : '';
    a.download = `logs_${content.driverId}_${content.date}${flavorSuffix}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <FileText className="text-blue-500" />
          Diagnostics Logs
        </h1>
        <div className="text-sm text-gray-500">
          {userFolders.length} users, {total} total files
        </div>
      </div>

      {truncated && (
        <div className="card mb-6 border-yellow-300 bg-yellow-50 text-sm text-yellow-800">
          Not every log could be loaded at once, so some drivers or older logs are missing from
          this list. Filter by driver ID to see a driver's full history.
        </div>
      )}

      {/* Search */}
      <div className="card mb-6">
        <div className="flex gap-4 items-center flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search by driver ID..."
              value={driverIdFilter}
              onChange={(e) => setDriverIdFilter(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <AppFilterSelect value={appFilter} onChange={setAppFilter} />

          {driverIdFilter && (
            <button
              onClick={() => setDriverIdFilter('')}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900"
            >
              Clear filter
            </button>
          )}
        </div>
      </div>

      {/* Folder list */}
      {loading ? (
        <div className="card">
          <div className="animate-pulse space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-16 bg-gray-200 rounded" />
            ))}
          </div>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      ) : userFolders.length === 0 ? (
        <div className="card text-center py-12">
          <FileText size={48} className="mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500">No diagnostics logs found</p>
          <p className="text-sm text-gray-400 mt-2">
            Logs will appear here when users send diagnostics from the app
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {userFolders.map((folder) => {
            const isExpanded = expandedFolders.has(folder.driverId);

            return (
              <div key={folder.driverId} className="card overflow-hidden">
                {/* Folder header */}
                <button
                  onClick={() => toggleFolder(folder.driverId)}
                  className="w-full flex items-center gap-3 p-4 hover:bg-gray-50 transition-colors text-left"
                >
                  <div className="text-gray-400">
                    {isExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                  </div>
                  <div className="text-yellow-500">
                    {isExpanded ? <FolderOpen size={24} /> : <Folder size={24} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <User size={14} className="text-gray-400" />
                      <span className="font-medium text-gray-900 truncate">
                        {folder.userEmail || folder.userName || folder.driverId}
                      </span>
                      {(folder.userEmail || folder.userName) && (
                        <span className="text-xs text-gray-400 font-mono truncate">
                          ({folder.driverId})
                        </span>
                      )}
                      {folder.hasFedex && <AppBadge flavor="fedex" />}
                    </div>
                    <div className="text-sm text-gray-500 mt-0.5">
                      {folder.files.length} log{folder.files.length !== 1 ? 's' : ''} &middot;{' '}
                      {formatBytes(folder.totalSize)} total
                    </div>
                  </div>
                  <div className="text-sm text-gray-400">
                    Updated {formatDistanceToNow(new Date(folder.latestUpload), { addSuffix: true })}
                  </div>
                </button>

                {/* Folder contents */}
                {isExpanded && (
                  <div className="border-t border-gray-200 bg-gray-50">
                    <table className="w-full">
                      <thead className="bg-gray-100 border-b border-gray-200">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider pl-14">
                            Date
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Size
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Uploaded
                          </th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {folder.files.map((file) => (
                          <tr key={file.key} className="hover:bg-gray-100">
                            <td className="px-4 py-3 text-sm text-gray-900 pl-14">
                              <div className="flex items-center gap-2">
                                <FileText size={16} className="text-gray-400" />
                                {format(new Date(file.date), 'MMM d, yyyy')}
                                <AppBadge flavor={file.appFlavor} />
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">
                              {formatBytes(file.size)}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-500">
                              {formatDistanceToNow(new Date(file.uploaded), { addSuffix: true })}
                            </td>
                            <td className="px-4 py-3 text-right">
                              <button
                                onClick={() => viewFile(file)}
                                className="inline-flex items-center gap-1 px-3 py-1 text-sm text-primary-600 hover:text-primary-800 hover:bg-primary-50 rounded"
                              >
                                <Eye size={16} />
                                View
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Log viewer modal */}
      {(selectedFile || loadingFile) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  {loadingFile ? 'Loading...' : `Logs: ${selectedFile?.driverId}`}
                </h2>
                {selectedFile && (
                  <p className="text-sm text-gray-500">
                    {format(new Date(selectedFile.date), 'MMMM d, yyyy')} - {formatBytes(selectedFile.size)}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2">
                {selectedFile && (
                  <button
                    onClick={() => downloadFile(selectedFile)}
                    className="inline-flex items-center gap-1 px-3 py-1 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded"
                  >
                    <Download size={16} />
                    Download
                  </button>
                )}
                <button
                  onClick={() => setSelectedFile(null)}
                  className="p-1 text-gray-400 hover:text-gray-600"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-auto p-4">
              {loadingFile ? (
                <div className="animate-pulse space-y-2">
                  {[...Array(20)].map((_, i) => (
                    <div key={i} className="h-4 bg-gray-200 rounded w-full" style={{ width: `${Math.random() * 40 + 60}%` }} />
                  ))}
                </div>
              ) : selectedFile ? (
                <pre className="text-xs font-mono text-gray-800 whitespace-pre-wrap break-words">
                  {selectedFile.content}
                </pre>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
